import axios, {AxiosInstance, AxiosResponse, InternalAxiosRequestConfig,} from "axios";
import {inject, injectable} from "inversify";

import {IApiService} from "@/injection/interfaces";
import {TYPES} from "@/injection/types";
import "@/extensions/date.extensions";
import {ITokenResponse} from "@/types/responses/tokenResponse";
import {useApiStore} from "@/stores/apiStore";

@injectable()
export class ApiService implements IApiService {
  _httpClient: AxiosInstance;
  _accessToken: string;
  _accessTokenExpires: number;
  _tokenRequestedAt: Date;

  constructor(@inject(TYPES.AxiosInstance) httpClient: AxiosInstance) {
    this._httpClient = httpClient;
    this._accessToken = "";
    this._accessTokenExpires = 0;
    this._tokenRequestedAt = new Date();

    /*
        Attaching the accessToken to the request header. The access token is stored in cookies by ApiAuthenticationController
        Keep track of the expiresIn so we do not poke the backend every time we make a request.
        AccessToken and RefreshToken rotation is handled in by Umbraco OpenId Connect so when we call the
        ApiAuthenticationController we always have a valid accessToken.
    */
    this._httpClient.interceptors.request.use(
      async (config) => {
        // Checking if the AccessToken has expired. If it is expires, we call the backend to get the current valid token
        // otherwise we do nothing since the bearer is already set with the valid token.
        if (this._accessToken === "" || this._accessTokenExpires === 0) {
          await this.askUmbracoBackEndForNewTokens(config, false);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this._httpClient.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        if (error.request.status === 401 && originalRequest._retry) {
          return Promise.reject(error);
        }

        if (error.request.status == 401) {
          originalRequest._retry = true;
          return await this.askUmbracoBackEndForNewTokens(
            originalRequest,
            true
          );
        }
        return Promise.reject(error);
      }
    );
  }

  public headersWithJsonContentType() {
    return {
      headers: {
        "Content-Type": 'application/json',
      },
    };
  }

  public headersWithFormDataContentType() {
    return {
      headers: {
        "Content-Type": '"multipart/form-data"',
      },
    };
  }

  public buildEmptyBody(): string {
    return '{}'
  }

  private async askUmbracoBackEndForNewTokens(
    config: InternalAxiosRequestConfig<any>,
    retryRequest: boolean
  ) {
    try {
      return await axios
        .get(
          `${process.env.VUE_APP_WEBSITE_BASE_URL}/api-authentication/refresh-token`
        )
        .then((response: AxiosResponse<ITokenResponse>) => {
          if (!response.data) return;

          const tokens = response.data;
          if (!tokens.accessToken) {
            // refresh token is expired
            this.logoutUserAndRedirectToHomePage();
            return;
          }
          this._accessToken = response.data.accessToken;
          this._accessTokenExpires = response.data.expiresIn;
          this._tokenRequestedAt = new Date();

          const bearer = `Bearer ${this._accessToken}`;
          config.headers.Authorization = bearer;
          this._httpClient.defaults.headers.common['Authorization'] = bearer;

          if (retryRequest) {
            return this._httpClient(config)
          }
        });
    } catch (error) {
      this.logoutUserAndRedirectToHomePage()
      return Promise.reject(error);
    }
  }

  private logoutUserAndRedirectToHomePage() {
    const apiStore = useApiStore();
    apiStore.setNeedToLogout(true);
    // A watcher is setup in the component LogoutPopup, it will handle the rest.
  }
}